/**
 * Iter-124 — Guide ordering helpers.
 *
 * Single source of truth for the "Pinned → Featured → Newest" ordering
 * the user spec'd for the Guides page + Homepage guide section.
 *
 * Article shape (loosely typed — these all come from /api/articles or
 * lib/guides.js#guidesAsArticles, which set the same keys):
 *   { slug, is_pinned?, is_featured?, published_at?, updated_at?,
 *     last_reviewed_at? }
 *
 * The helpers are intentionally pure + deterministic — no React, no
 * hooks — so they're reusable from the Homepage `ContentHub`, the
 * Guides hub `ArticlesIndex`, sitemap builders, and the admin preview.
 */

const _ts = (a) =>
  new Date(
    a.last_reviewed_at || a.updated_at || a.published_at || 0,
  ).getTime() || 0;

/**
 * Tier-comparator used by both `sortGuides` and `pickHomepageGuides`.
 * Lower return value = sorts earlier.
 *
 *   1. Pinned guides first
 *   2. Featured guides next
 *   3. Then newest published / updated guide
 *
 * Falls back to slug ordering when timestamps tie (stable across runs).
 */
const compareGuides = (a, b) => {
  const aPinned = !!a.is_pinned;
  const bPinned = !!b.is_pinned;
  if (aPinned !== bPinned) return aPinned ? -1 : 1;

  const aFeatured = !!a.is_featured;
  const bFeatured = !!b.is_featured;
  if (aFeatured !== bFeatured) return aFeatured ? -1 : 1;

  const at = _ts(a);
  const bt = _ts(b);
  if (at !== bt) return bt - at;

  return (a.slug || "").localeCompare(b.slug || "");
};

/**
 * Sort a full guide list using the pinned → featured → newest order.
 * Pure: returns a new array, leaves the input alone.
 */
export const sortGuides = (guides) =>
  Array.isArray(guides) ? [...guides].sort(compareGuides) : [];

/**
 * Pick the N best guides for the homepage editorial slot.
 *
 * Rules per spec:
 *   - Pinned + featured guides take priority
 *   - If there are fewer than N qualifying featured/pinned guides, fill
 *     the remaining slots with the newest published guides
 *   - Never include the same guide twice
 *
 * Returns at most `count` items (default 6).
 */
export const pickHomepageGuides = (guides, count = 6) => {
  if (!Array.isArray(guides) || guides.length === 0) return [];
  const featuredOrPinned = guides
    .filter((g) => g.is_featured || g.is_pinned)
    .sort(compareGuides);

  if (featuredOrPinned.length >= count) {
    return featuredOrPinned.slice(0, count);
  }

  const usedSlugs = new Set(featuredOrPinned.map((g) => g.slug));
  const newest = guides
    .filter((g) => !usedSlugs.has(g.slug))
    .sort(compareGuides);

  return [...featuredOrPinned, ...newest].slice(0, count);
};
